import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/Dialog";

export default function BoosterIssuesModal({ open, onOpenChange, macros, onResolved }) {
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    if (macros && macros.length > 0) {
      setEntries(
        macros.map((m) => ({
          ...m,
          dot_phrase: m.dot_phrase || "",
          keywords: m.keywords || "",
          content: m.content || "",
        }))
      );
    }
  }, [macros]);

  const handleChange = (index, field, value) => {
    setEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        if (field === "dot_phrase" && value && !value.startsWith(".")) {
          value = "." + value;
        }
        return { ...entry, [field]: value };
      })
    );
  };

  const allResolved = entries.every(
    (e) => e.dot_phrase && e.keywords && e.content
  );

  const handleSave = () => {
    if (!allResolved) return;
    onResolved(entries.map(({ issues, ...rest }) => rest));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Issues Detected</DialogTitle>
          <DialogDescription>
            Knowtex detected issues with {entries.length} dot phrase{entries.length !== 1 ? "s" : ""}. Please fill in the missing required fields.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {entries.map((entry, index) => {
            const fieldLabels = { dot_phrase: "Dot Phrase", keywords: "Keywords", content: "Content" };
            const missingLabels = (entry.issues || []).map((f) => fieldLabels[f] || f);

            return (
              <div key={index} className="flex flex-col gap-3 p-4 border border-gray-200 rounded-lg">
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
                  <svg className="w-5 h-5 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span>
                    Missing required field{missingLabels.length !== 1 ? "s" : ""}: <strong>{missingLabels.join(", ")}</strong>
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dot Phrase <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={entry.dot_phrase}
                    onChange={(e) => handleChange(index, "dot_phrase", e.target.value)}
                    placeholder="Enter dot phrase (e.g. .example)"
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      !entry.dot_phrase && entry.issues.includes("dot_phrase")
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {!entry.dot_phrase && entry.issues.includes("dot_phrase") && (
                    <p className="mt-1 text-xs text-red-600">This field is required</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Keywords <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={entry.keywords}
                    onChange={(e) => handleChange(index, "keywords", e.target.value)}
                    placeholder="Enter keywords (comma separated)"
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      !entry.keywords && entry.issues.includes("keywords")
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {!entry.keywords && entry.issues.includes("keywords") && (
                    <p className="mt-1 text-xs text-red-600">This field is required</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={entry.content}
                    onChange={(e) => handleChange(index, "content", e.target.value)}
                    placeholder="Enter dot phrase content"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-y ${
                      !entry.content && entry.issues.includes("content")
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {!entry.content && entry.issues.includes("content") && (
                    <p className="mt-1 text-xs text-red-600">This field is required</p>
                  )}
                </div>
              </div>
            );
          })}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!allResolved}
              className="px-4 py-2 text-sm font-medium text-white bg-ktx-purple rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save & Continue
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
